/**
 * DynamoDB caps each response at 1MB regardless of the requested `limit`, so a
 * single Amplify `.list()` call can return `items: []` with a `nextToken` set
 * when a page's items are all filtered out server-side but more matching data
 * exists on later pages. This loops on `nextToken` until every page has been
 * fetched, so callers always get the complete result set from one await.
 * Pass onPage to receive accumulated results after each page loads, for
 * progressive rendering while the rest streams in.
 */
export const listAll = async <T,>(
  listFn: (
    nextToken?: string,
  ) => Promise<{ data: T[]; errors?: any[]; nextToken?: string | null }>,
  onPage?: (accumulated: T[]) => void,
): Promise<T[]> => {
  let all: T[] = [];
  let nextToken: string | undefined;

  do {
    const { data, errors, nextToken: next } = await listFn(nextToken);
    if (errors) {
      throw new Error(errors[0]?.message || "Failed to fetch all pages");
    }
    if (data?.length) all = all.concat(data);
    onPage?.(all);
    nextToken = next ?? undefined;
  } while (nextToken);

  return all;
};
